import { getTraitDailyDelta } from '../data/personalityTraits';

/**
 * Pre-defined contract categories the matchmaker iterates over. Drawn from
 * /app/frontend/src/data/contracts.js — keep in sync if new categories are
 * added.
 */
const CONTRACT_CATEGORIES = [
  'Messagerie / Colis',
  'BTP / Matériaux',
  'Frigorifique',
  'Spécialisé',
  'Export EU',
  'Vrac / Palettes',
];

/**
 * Per-trait maximum positive contribution. Sum across all traits gives a
 * proxy for the "best possible" daily delta the driver could achieve under
 * an ideal assignment (no conflicts considered — best-case proxy).
 */
const TRAIT_MAX_POSITIVE = {
  'old-school': 0,
  'tech-savvy': 0.10,
  'ambitieux': 0.15,
  'casanier': 0.10,
  'introverti': 0,
  'aventurier': 0.15,
  'prudent': 0.10,
  'fier': 0.10,
  'rebelle': 0,
  'pragmatique': 0,
  'perfectionniste': 0.10,
  'sceptique': 0,
};

const TIER_LABELS = {
  1: 'Tier 1 — Fourgon',
  2: 'Tier 2 — Porteur',
  3: 'Tier 3 — Semi',
  4: 'Tier 4 — Spécialisé',
  5: 'Tier 5 — Prestige',
};

/**
 * Project how many days until a driver crosses the satisfaction critical
 * threshold (< 30) given his/her current mission. Returns Infinity if the
 * driver is gaining sat (or the delta is exactly zero — projection
 * meaningless), or null if the driver is not on a mission.
 */
const projectDaysToCritical = (driver, totalDelta) => {
  if (driver.status !== 'active') return null;
  if (totalDelta >= 0) return Infinity;
  const buffer = (driver.satisfaction || 0) - 30;
  if (buffer <= 0) return 0;
  // |totalDelta| is the net daily drop. divide buffer by it.
  return Math.ceil(buffer / Math.abs(totalDelta));
};

/**
 * Compute the per-mission preference of a driver: for each contract category,
 * evaluate the trait delta against an average tier-3 vehicle to surface the
 * structural fit. We also test extreme tiers (1 and 5) to detect tier
 * preferences (e.g. 'fier' likes Tier 4+).
 *
 * Returns:
 *   {
 *     bestContracts: [{ category, delta }, ...]   // sorted desc, top 2
 *     worstContracts: [{ category, delta }, ...]  // sorted asc (most negative), top 2
 *     bestTier: { id, delta }                     // the tier that gives the highest delta
 *     worstTier: { id, delta }                    // optional, only if non-zero
 *     currentDelta: number                        // delta with the actual assignment (or 0)
 *     daysToCritical: number|Infinity|null        // projection
 *   }
 */
export const analyzeDriverCompatibility = (driver, vehicles, contracts) => {
  if (!driver || !(driver.personalityTraits || []).length) {
    return {
      bestContracts: [],
      worstContracts: [],
      bestTier: null,
      worstTier: null,
      currentDelta: 0,
      daysToCritical: null,
      hasImpactingTraits: false,
      personalityROI: null,
      maxPossibleDelta: 0,
      bestCombo: null,
    };
  }

  // Evaluate each category with a "neutral" mid-tier vehicle (tier 3, wear 30).
  const categoryScores = CONTRACT_CATEGORIES.map(category => {
    const neutralVehicle = { tier: 3, wear: 30, dataId: 'neutral' };
    const fakeContract = { category };
    return { category, delta: getTraitDailyDelta(driver, neutralVehicle, fakeContract) };
  });

  const bestContracts = [...categoryScores]
    .filter(c => c.delta > 0)
    .sort((a, b) => b.delta - a.delta)
    .slice(0, 2);
  const worstContracts = [...categoryScores]
    .filter(c => c.delta < 0)
    .sort((a, b) => a.delta - b.delta)
    .slice(0, 2);

  // Tier preference: evaluate with neutral category (no contract effect).
  const tierScores = [1, 2, 3, 4, 5].map(tier => {
    const v = { tier, wear: 30, dataId: 'gas' };
    return { tier, delta: getTraitDailyDelta(driver, v, null) };
  });
  const bestTierEntry = [...tierScores].sort((a, b) => b.delta - a.delta)[0];
  const worstTierEntry = [...tierScores].sort((a, b) => a.delta - b.delta)[0];

  // Current mission delta — use actual assignment.
  const currentVehicle = (vehicles || []).find(v => v.id === driver.assignedVehicleId);
  const currentContract = (contracts || []).find(c => c.driverId === driver.id);
  const currentDelta = getTraitDailyDelta(driver, currentVehicle, currentContract);

  // ── ROI Personnalité ────────────────────────────────────────────────────
  // Theoretical "best-case" delta: sum of positive trait potentials.
  const maxPossibleDelta = (driver.personalityTraits || [])
    .reduce((sum, traitId) => sum + (TRAIT_MAX_POSITIVE[traitId] || 0), 0);

  // Find the single BEST contract+tier combination for this driver via
  // exhaustive iteration (small space: 6 categories × 5 tiers × 2 engines).
  let bestComboDelta = 0;
  let bestCombo = null;
  for (const category of CONTRACT_CATEGORIES) {
    for (const tier of [1, 2, 3, 4, 5]) {
      for (const engine of ['gas', 'electric']) {
        const v = { tier, wear: 20, dataId: engine === 'electric' ? 'fx-electric-zeroload' : 'gas-generic' };
        const c = { category };
        const d = getTraitDailyDelta(driver, v, c);
        if (d > bestComboDelta) {
          bestComboDelta = d;
          bestCombo = { category, tier, engine, delta: d };
        }
      }
    }
  }

  // ROI ratio: how much of the max potential is currently being captured.
  // Defined only when there IS positive potential (denominator > 0). We use
  // currentDelta clamped to [0, max] so negative current values yield 0%.
  const roiNumerator = Math.max(0, currentDelta);
  const personalityROI = maxPossibleDelta > 0
    ? Math.round((roiNumerator / maxPossibleDelta) * 100)
    : null; // null = "pas de potentiel positif, pas de ROI à calculer"

  return {
    bestContracts,
    worstContracts,
    bestTier: bestTierEntry && bestTierEntry.delta > 0
      ? { id: bestTierEntry.tier, label: TIER_LABELS[bestTierEntry.tier], delta: bestTierEntry.delta }
      : null,
    worstTier: worstTierEntry && worstTierEntry.delta < 0
      ? { id: worstTierEntry.tier, label: TIER_LABELS[worstTierEntry.tier], delta: worstTierEntry.delta }
      : null,
    currentDelta,
    daysToCritical: projectDaysToCritical(driver, currentDelta),
    hasImpactingTraits: bestContracts.length > 0 || worstContracts.length > 0 || !!bestTierEntry || !!worstTierEntry,
    // ROI Personnalité
    personalityROI,
    maxPossibleDelta: Math.round(maxPossibleDelta * 100) / 100,
    bestCombo, // { category, tier, engine, delta } | null
  };
};
